const GUEST_KEY = 'clog-wishlist';
const EVENT_NAME = 'clog:wishlist-change';
const PROXY_PATH = '/apps/wishlist';

function customerId() {
  return String(window.Theme?.customer?.id || '').trim();
}

function storageKey() {
  const id = customerId();
  return id ? `${GUEST_KEY}:${id}` : GUEST_KEY;
}

function readList(key = storageKey()) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function writeList(items, key = storageKey()) {
  localStorage.setItem(key, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items } }));
}

function normalizeItems(input) {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const items = [];
  input.forEach((entry) => {
    const id = String(entry?.id || entry || '').replace(/\D/g, '');
    if (!id || seen.has(id)) return;
    seen.add(id);
    items.push({
      id,
      handle: String(entry?.handle || ''),
    });
  });
  return items;
}

function mergeLists(...lists) {
  const seen = new Set();
  const merged = [];
  lists.flat().forEach((item) => {
    const id = String(item?.id || '');
    if (!id || seen.has(id)) return;
    seen.add(id);
    merged.push({
      id,
      handle: String(item.handle || ''),
    });
  });
  return merged;
}

let remoteSaveTimer = 0;

async function fetchRemoteWishlist() {
  if (!customerId()) return null;
  try {
    const response = await fetch(PROXY_PATH, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return normalizeItems(data.items || data.ids || []);
  } catch (error) {
    return null;
  }
}

async function saveRemoteWishlist(items) {
  if (!customerId()) return;
  try {
    await fetch(PROXY_PATH, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
  } catch (error) {
    // Keep the local list if the app is not installed yet.
  }
}

function scheduleRemoteSave(items) {
  if (!customerId()) return;
  window.clearTimeout(remoteSaveTimer);
  remoteSaveTimer = window.setTimeout(() => {
    saveRemoteWishlist(items);
  }, 350);
}

function seedFromTheme() {
  return normalizeItems(window.Theme?.customer?.wishlist);
}

export const ClogWishlist = {
  getItems() {
    return readList();
  },

  getIds() {
    return this.getItems().map((item) => String(item.id));
  },

  has(productId) {
    return this.getIds().includes(String(productId));
  },

  toggle(product) {
    const id = String(product.id || '');
    if (!id) return false;

    const items = this.getItems();
    const index = items.findIndex((item) => String(item.id) === id);
    if (index > -1) {
      items.splice(index, 1);
      writeList(items);
      scheduleRemoteSave(items);
      return false;
    }

    items.unshift({
      id,
      handle: product.handle || '',
    });
    writeList(items);
    scheduleRemoteSave(items);
    return true;
  },

  remove(productId) {
    const items = this.getItems().filter((item) => String(item.id) !== String(productId));
    writeList(items);
    scheduleRemoteSave(items);
  },
};

function syncButtons(root = document) {
  root.querySelectorAll('[data-clog-wishlist]').forEach((button) => {
    const active = ClogWishlist.has(button.dataset.productId);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active ? button.dataset.labelSaved || 'Saved to wishlist' : button.dataset.labelSave || 'Save to wishlist';
    button.setAttribute('aria-label', label);
  });
}

function syncCounts(root = document) {
  const count = ClogWishlist.getItems().length;
  root.querySelectorAll('[data-clog-wishlist-count]').forEach((el) => {
    el.textContent = count > 0 ? String(count) : '';
    el.hidden = count === 0;
    el.closest('[data-clog-wishlist-count-wrap]')?.classList.toggle('is-empty', count === 0);
  });
}

function bindButtons(root = document) {
  root.querySelectorAll('[data-clog-wishlist]').forEach((button) => {
    if (button.dataset.clogWishlistBound) return;
    button.dataset.clogWishlistBound = 'true';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      ClogWishlist.toggle({
        id: button.dataset.productId,
        handle: button.dataset.productHandle,
      });
    });
  });
}

class ClogWishlistPage extends HTMLElement {
  connectedCallback() {
    this.#load();
    window.addEventListener(EVENT_NAME, this.#onChange);
  }

  disconnectedCallback() {
    window.removeEventListener(EVENT_NAME, this.#onChange);
  }

  #onChange = () => {
    this.#load();
  };

  async #load() {
    const ids = ClogWishlist.getIds();
    const empty = this.querySelector('[data-clog-wishlist-empty]');
    const grid = this.querySelector('[data-clog-wishlist-grid]');
    if (!grid) return;

    if (!ids.length) {
      grid.replaceChildren();
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;

    const searchUrl = window.Theme?.routes?.search_url || '/search';
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'clog-wishlist');

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const remoteItems = [...doc.querySelectorAll('[data-clog-wishlist-item]')];
      const ordered = ids
        .map((id) => remoteItems.find((item) => String(item.dataset.productId) === id))
        .filter(Boolean);

      grid.replaceChildren(...ordered);
      bindButtons(grid);
      syncButtons(grid);
      this.#bindRemoves(grid);
    } catch (error) {
      if (empty) empty.hidden = false;
    }
  }

  #bindRemoves(root) {
    root.querySelectorAll('[data-clog-wishlist-remove]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        ClogWishlist.remove(button.dataset.productId);
      });
    });
  }
}

async function syncAccountWishlist() {
  if (!customerId()) return;

  const guestItems = readList(GUEST_KEY);
  const localItems = readList();
  const themeItems = seedFromTheme();
  const remoteItems = await fetchRemoteWishlist();
  const merged = mergeLists(localItems, guestItems, themeItems, remoteItems || []);

  writeList(merged);
  localStorage.removeItem(GUEST_KEY);

  if (remoteItems) {
    await saveRemoteWishlist(merged);
  }
}

function init() {
  if (customerId()) {
    const seeded = mergeLists(readList(), seedFromTheme());
    writeList(seeded);
    syncAccountWishlist();
  } else {
    mergeLists(readList());
  }

  bindButtons();
  syncButtons();
  syncCounts();
}

window.addEventListener(EVENT_NAME, () => {
  syncButtons();
  syncCounts();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

if (!customElements.get('clog-wishlist')) {
  customElements.define('clog-wishlist', ClogWishlistPage);
}
